import htmx from 'htmx.org'
window.htmx = htmx;

// History restores past htmx's cache must fetch the full page: the server
// answers HX-Request with a shell-less partial, which htmx would swap into
// <body>. (htmx's own docs recommend this whenever HX-Request selects partials.)
htmx.config.historyRestoreAsHxRequest = false;
// A 422 carries a re-rendered form with validation messages: swap it, but keep
// it an error so success handlers (detail.successful) do not fire. Toast-only
// errors send HX-Reswap: none from the server and swap nothing.
// htmx.config is typed Record<string, unknown> in globals.d.ts, hence the cast.
(htmx.config.responseHandling as { code: string; swap: boolean; error: boolean }[])
    .unshift({ code: "422", swap: true, error: true });

import persist from "@alpinejs/persist";
import collapse from "@alpinejs/collapse";
import Alpine from 'alpinejs'

import { SSEConnection } from "./sse";
import { SSELifecycle } from "./sse_lifecycle";
import { navigateMain } from "./nav_click";
import { resetScrollOnNavigation } from "./nav_scroll";
import { settleRoot } from "./settle_root";
import { ToastHandler } from "./toast";
import { registerFeedbackBubble } from "./feedback_bubble";
import { shouldIgnoreGlobalShortcut } from "./shortcut_guard";
import { registerDatePicker } from "./date_picker";
import { registerDecimalInput } from "./decimal_input";
import { markAlpineGenerated, stripAlpineGenerated } from "./alpine_history";
import { ConfirmDialog, handleConfirmEscape, handleHtmxConfirm } from "./confirm_dialog";

// Add Alpine instance to window object.
Alpine.plugin(persist);
Alpine.plugin(collapse);
window.Alpine = Alpine;
window.navigateMain = navigateMain;


window.__toastHandler = Alpine.reactive(new ToastHandler()) as typeof window.__toastHandler;
Alpine.store("toast", window.__toastHandler);
// Per-element wrapper inheriting from the shared store: Alpine injects its
// magics ($nextTick, …) onto this throwaway object instead of the singleton,
// so recreating the toast div on htmx history restore (browser back) can't
// redefine them. Reads/writes (toasts, start(), remove()) resolve through the
// prototype chain to the same reactive store, preserving shared state.
Alpine.data("toastComponent", () => Object.create(Alpine.store("toast")));

// The product's confirm dialog (templates.ConfirmDialogHTML): the only way
// staff UI asks a question. data-confirm on an htmx element, or appConfirm()
// from script; never window.confirm.
const confirmDialog = Alpine.reactive(new ConfirmDialog()) as ConfirmDialog;
Alpine.store("confirm", confirmDialog);
window.appConfirm = (message: string) => confirmDialog.ask(message);
// A confirmed element that a swap removed meanwhile: say so instead of doing
// nothing. The text is rendered translated on the dialog root.
const confirmGone = () => {
    const el = document.querySelector<HTMLElement>("[data-confirm-dialog]");
    const msg = el?.dataset.goneMessage;
    if (msg) window.__toastHandler.add(msg, "error", 5000);
};
document.addEventListener("htmx:confirm", (e) => handleHtmxConfirm(e, window.appConfirm, confirmGone));
window.addEventListener("keydown", (e) => handleConfirmEscape(e, confirmDialog), true);
registerFeedbackBubble(Alpine);
registerDatePicker(Alpine);
registerDecimalInput(document);

// --- Tenant date display -------------------------------------------------
// Name tables come from the #display-names JSON block emitted by
// templates.Head. The per-property format code is NOT global — each Alpine
// component receives its own `dateFormat` in x-data, because that value varies
// per request while these tables are constant for the process.

const EN_FALLBACK: DisplayNames = {
    monthsLong: ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'],
    monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    weekdaysLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    weekdaysNarrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    am: 'AM',
    pm: 'PM',
    patterns: {
        month_year_long: '%{month} %{year}',
        month_year_short: '%{month} %{year}',
        weekday_day_month: '%{weekday}, %{day} %{month}',
        weekday_day_month_year: '%{weekday}, %{day} %{month} %{year}',
    },
};

function readDisplayNames(): DisplayNames {
    const el = document.getElementById('display-names');
    if (!el || !el.textContent) return EN_FALLBACK;
    try {
        const parsed = JSON.parse(el.textContent) as Partial<DisplayNames>;
        // patterns is merged per key, not replaced wholesale: NamesJSON omits
        // any key whose translation does not resolve, so a partial map is a
        // legitimate payload for an incomplete catalog. A top-level spread
        // would leave the missing key undefined and make fillPattern throw on
        // .replace — breaking every calendar render on the page.
        return {
            ...EN_FALLBACK,
            ...parsed,
            patterns: { ...EN_FALLBACK.patterns, ...(parsed.patterns ?? {}) },
        };
    } catch {
        // A malformed block must never break the page — every date on it would
        // disappear. Degrade to English and keep rendering.
        return EN_FALLBACK;
    }
}

const displayNames = readDisplayNames();

/**
 * Substitutes %{token} placeholders — ctxi18n's interpolation syntax, so the
 * same catalog pattern strings work server-side and here. Unknown tokens
 * are left in place rather than blanked, so a typo is visible instead of
 * silently deleting text.
 */
function fillPattern(pattern: string, params: Record<string, string>): string {
    return pattern.replace(/%\{(\w+)\}/g, (match, key: string) =>
        Object.prototype.hasOwnProperty.call(params, key) ? params[key] : match);
}

/** Splits an ISO YYYY-MM-DD string. Returns null for anything else. */
function isoParts(iso: string): { y: string; m: string; d: string } | null {
    if (!iso) return null;
    const parts = iso.split('-');
    if (parts.length !== 3) return null;
    return { y: parts[0], m: parts[1], d: parts[2] };
}

window.__display = {
    names: displayNames,

    // Mirrors display.toGoLayout in Go. Keep the two in sync.
    formatDate(iso: string, dateFormat: string): string {
        const p = isoParts(iso);
        if (!p) return '';
        switch (dateFormat) {
            case 'DD.MM.YYYY': return `${p.d}.${p.m}.${p.y}`;
            case 'MM/DD/YYYY': return `${p.m}/${p.d}/${p.y}`;
            default: return `${p.y}-${p.m}-${p.d}`;
        }
    },

    // No dateFormat parameter: this label is built entirely from the
    // catalog pattern and the name tables, so the numeric format code has
    // nothing to contribute.
    formatWeekdayDayMonthYear(iso: string): string {
        const dt = new Date(`${iso}T00:00:00`);
        if (Number.isNaN(dt.getTime())) return '';
        return fillPattern(displayNames.patterns.weekday_day_month_year, {
            weekday: displayNames.weekdaysShort[dt.getDay()],
            day: String(dt.getDate()),
            month: displayNames.monthsShort[dt.getMonth()],
            year: String(dt.getFullYear()),
        });
    },

    formatWeekdayDayMonth(iso: string): string {
        const dt = new Date(`${iso}T00:00:00`);
        if (Number.isNaN(dt.getTime())) return '';
        return fillPattern(displayNames.patterns.weekday_day_month, {
            weekday: displayNames.weekdaysShort[dt.getDay()],
            day: String(dt.getDate()),
            month: displayNames.monthsShort[dt.getMonth()],
        });
    },

    monthYearShort(iso: string): string {
        const dt = new Date(`${iso}T00:00:00`);
        if (Number.isNaN(dt.getTime())) return '';
        return fillPattern(displayNames.patterns.month_year_short, {
            month: displayNames.monthsShort[dt.getMonth()],
            year: String(dt.getFullYear()),
        });
    },

    monthYearLong(year: number, monthIndex: number): string {
        return fillPattern(displayNames.patterns.month_year_long, {
            month: displayNames.monthsLong[monthIndex],
            year: String(year),
        });
    },
};

// SelectDropdown — custom accessible select with icon, search and description support.
interface SelectDropdownCfg {
    val: string;
    label: string;
    icons: Record<string, string>;
    codeLabels: Record<string, string>;
    trailingTexts: Record<string, string>;
    statusDots?: Record<string, string>;
    optionLabels: Record<string, string>;
    labels: string[];
    name: string;
}
Alpine.data("selectDropdown", (cfg: SelectDropdownCfg) => ({
    isOpen: false,
    val: cfg.val,
    label: cfg.label,
    name: cfg.name,
    query: '' as string,
    icons: cfg.icons,
    codeLabels: cfg.codeLabels,
    trailingTexts: cfg.trailingTexts,
    statusDots: cfg.statusDots || {},
    optionLabels: cfg.optionLabels,
    labels: cfg.labels,
    reset(this: any) {
        this.val = '';
        this.label = '';
        this.$dispatch('sd-change', { name: this.name, value: '', label: '' });
    },
    toggle(this: any) {
        this.isOpen = !this.isOpen;
        if (this.isOpen) this.$nextTick(() => this.$refs.sdSearch?.focus());
    },
    close(this: any) { this.isOpen = false; this.query = ''; },
    pick(this: any, v: string, l: string) {
        this.val = v; this.label = l; this.isOpen = false; this.query = '';
        this.$dispatch('sd-change', { name: cfg.name, value: v, label: l });
        // Also fire a native change on the hidden input so parent @change handlers
        // (e.g. the rate-rule condition row's sync()) react with an already-updated
        // DOM. $nextTick lets Alpine flush :value before the event is dispatched,
        // avoiding the capture-phase race. Uses $root (the component's own x-data
        // element), not $el: pick() is called from an option button's @click, so
        // $el there is that button, not the dropdown root — querying inside it
        // never finds the hidden input, silently skipping the dispatch.
        this.$nextTick(() => {
            const inp = (this.$root as HTMLElement).querySelector<HTMLInputElement>('input[type="hidden"]');
            if (inp) inp.dispatchEvent(new Event('change', { bubbles: true }));
        });
    },
    matches(this: any, l: string): boolean {
        if (!this.query) return true;
        return l.toLowerCase().includes((this.query as string).toLowerCase());
    },
    get hasMatch(): boolean {
        return (this.labels as string[]).some((l: string) => this.matches(l));
    },
}));

// Checkbox-based multi-select. The server renders real checkboxes and a
// fallback summary; this component only adds open/close, the live summary and
// the select-all / clear actions. Uses $root (the component's x-data element),
// not $el, because methods run from child @click handlers where $el is the child.
Alpine.data("multiSelectDropdown", (cfg: { placeholder: string; manyTemplate: string }) => ({
    isOpen: false,
    query: '' as string,
    summary: '' as string,
    summaryIsPlaceholder: true,
    init(this: any) { this.refresh(); },
    boxes(this: any): HTMLInputElement[] {
        return Array.from((this.$root as HTMLElement).querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
    },
    refresh(this: any) {
        // Disabled boxes are not submitted, so they are not "selected".
        const picked = this.boxes().filter((b: HTMLInputElement) => b.checked && !b.disabled);
        this.summaryIsPlaceholder = picked.length === 0;
        if (picked.length === 0) this.summary = cfg.placeholder;
        else if (picked.length === 1) this.summary = picked[0].closest('label')?.textContent?.trim() ?? '';
        else this.summary = cfg.manyTemplate.replace('{count}', String(picked.length));
    },
    setAll(this: any, on: boolean) {
        // Act only on the options the search leaves visible.
        this.boxes().forEach((b: HTMLInputElement) => {
            if (!b.disabled && this.matches(b.closest('label')?.textContent ?? '')) b.checked = on;
        });
        this.refresh();
        // Programmatic checked changes fire no event; dispatch one so form-level
        // @change listeners (e.g. the live preview) react.
        (this.$root as HTMLElement).dispatchEvent(new Event('change', { bubbles: true }));
    },
    matches(this: any, l: string): boolean {
        if (!this.query) return true;
        return l.toLowerCase().includes((this.query as string).toLowerCase());
    },
    get hasMatch(): boolean {
        return this.boxes().some((b: HTMLInputElement) => this.matches(b.closest('label')?.textContent ?? ''));
    },
    toggle(this: any) {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            // Move focus into the panel: the search box when there is one, else the first enabled checkbox.
            this.$nextTick(() => {
                const panel = this.$refs.panel as HTMLElement | undefined;
                panel?.querySelector<HTMLElement>('input[type="text"], input[type="checkbox"]:not(:disabled)')?.focus();
            });
        }
    },
    close(this: any) { this.isOpen = false; this.query = ''; },
    // Escape closes the instance that holds focus and hands focus back to its trigger.
    closeAndFocus(this: any) {
        if (!this.isOpen) return;
        this.close();
        (this.$refs.trigger as HTMLElement | undefined)?.focus();
    },
}));

Alpine.data("dropdown", () => ({
    open: false,
    toggle(this: { open: boolean; position: () => void }) {
        this.open = !this.open;
        if (this.open) this.position();
    },
    // Alpine injects $nextTick, $el, $refs, $watch — typed via `this: any`.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    position(this: any) {
        this.$nextTick(() => {
            const button = this.$el as HTMLElement;
            const dropdown = this.$refs.dropdown as HTMLElement;
            const rect = button.getBoundingClientRect();

            dropdown.style.position = "fixed";
            dropdown.style.top = `${rect.bottom + window.scrollY}px`;
            dropdown.style.right = `${window.innerWidth - rect.right}px`;
            dropdown.style.zIndex = "999";

            // Reposition if would overflow viewport
            const dropdownRect = dropdown.getBoundingClientRect();
            if (dropdownRect.bottom > window.innerHeight) {
                dropdown.style.top = `${rect.top + window.scrollY - dropdownRect.height}px`;
            }
        });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    init(this: any) {
        this.$watch("open", (value: boolean) => {
            if (value) this.position();
        });
    },
}));

// Start Alpine on DOMContentLoaded so per-module bundles loaded from <body>
// have time to register Alpine.magic / Alpine.data / Alpine.store via
// 'alpine:init' listeners BEFORE Alpine walks the DOM. Module scripts (head
// and body) execute in document order after parsing but BEFORE
// DOMContentLoaded, so by the time DCL fires every per-module bundle has
// attached its registration listeners.
//
// document.readyState is 'interactive' here (modules execute pre-DCL), so
// the addEventListener call is sufficient and unconditional. Module-cloak
// (foundation x-cloak rule) hides any Alpine-controlled visible content
// during the one-tick gap; the body's dark-mode class is pre-applied via
// the Head() inline script (see main.templ).
document.addEventListener('DOMContentLoaded', () => Alpine.start());

// --- CSRF Protection ---
// Reads the CSRF token from the _csrf cookie (set by Echo CSRF middleware).
// Automatically injects it into HTMX requests (via header) and regular form
// submissions (via hidden field). No per-form template changes needed.
function csrfToken(): string {
    const match = document.cookie.match(/(?:^|;\s*)_csrf=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : "";
}
window.csrfToken = csrfToken;

// HTMX: add X-CSRF-Token header to every request.
document.body.addEventListener("htmx:configRequest", ((event: CustomEvent) => {
    event.detail.headers["X-CSRF-Token"] = csrfToken();
    // X-App-Shell (base.AppShellHeader): tells the server this page has the
    // app shell's #main-content, so Renderer.Redirect may answer with an
    // in-place HX-Location swap. Pages without it (guest booking engine,
    // full-screen pages) get HX-Redirect instead; an HX-Location aimed at a
    // missing #main-content would fail with htmx:targetError.
    if (document.getElementById("main-content")) {
        event.detail.headers["X-App-Shell"] = "1";
    }
}) as EventListener);

// Regular forms: inject hidden _csrf field before submission.
document.addEventListener("submit", (event: Event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    // getAttribute: a control named "method" shadows form.method.
    if ((form.getAttribute("method") ?? "get").toUpperCase() === "GET") return;
    if (form.querySelector('input[name="_csrf"]')) return;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "_csrf";
    input.value = csrfToken();
    form.appendChild(input);
});

// The body scroll lock is DERIVED from the DOM, never assigned by an individual
// modal. A modal removed by an out-of-band swap never runs a close handler, so a
// lock applied on open would leak and leave the page unscrollable until the next
// full load. Each ui.Modal binds data-modal-open="1" while it is open; the lock
// is simply whether any such element is still in the document, which also makes
// stacked modals behave (the page stays locked while any one remains open).
export function syncBodyScrollLock(): void {
    document.body.style.overflow =
        document.querySelector('[data-modal-open="1"]') ? "hidden" : "";
}

window.__syncBodyScrollLock = syncBodyScrollLock;

// Back-button snapshots must not carry Alpine-generated elements: see
// alpine_history.ts.
document.body.addEventListener("htmx:beforeHistorySave", ((event: CustomEvent) => {
    markAlpineGenerated(event.detail.historyElt as Element);
}) as EventListener);
document.body.addEventListener("htmx:historyCacheHit", ((event: CustomEvent) => {
    event.detail.item.content = stripAlpineGenerated(event.detail.item.content as string);
}) as EventListener);

// Re-initialize HTMX and Alpine on content loaded via HTMX navigation.
// Module scripts (<script type="module">) only execute once per URL (browser
// de-duplication), so page-specific init code won't re-run on subsequent HTMX
// visits. Each module script registers its init function into __alpineInits
// on first load. This global handler calls all registered inits on every swap.
//
// Uses event.detail.target (the swapped-into element) rather than .elt (the
// element that triggered the request) — they differ whenever hx-target
// points somewhere other than the triggering element itself (e.g. a button
// appending a row into a sibling list via hx-swap="beforeend"), in which
// case .elt never contains the new content at all and initTree/process
// would silently no-op on it. An outerHTML swap detaches its target, so
// settleRoot then falls back to the settled element the event fired on (the new
// content); re-initializing the detached target would run Alpine directives
// with no data stack (see settle_root.ts).
document.body.addEventListener("htmx:afterSettle", ((event: CustomEvent) => {
    const target = settleRoot(event.detail.target as Element | null, event.target);
    if (!target) return;
    htmx.process(target);
    window.Alpine.initTree(target);
    Object.entries(window.__alpineInits || {}).forEach(([selector, init]) => {
        target.querySelectorAll(selector).forEach(init);
    });
}) as EventListener);

// Registered as its own listener rather than called from the handler above:
// that handler returns early when settleRoot finds no connected root, and runs
// htmx.process/Alpine.initTree first — either of which can throw on bad swapped
// content. A skipped or thrown sync would leave the page permanently unscrollable.
//
// pageshow covers the one genuinely uncovered case: a native bfcache restore of a
// non-boosted navigation fires no htmx event at all. htmx:historyRestore is
// belt-and-braces — htmx's restoreHistory() swaps into details.historyElt, which
// defaults to document.body here (no [hx-history-elt] is defined), so doSettle()
// already fires htmx:afterSettle on document.body before historyRestore. Kept in
// case a future [hx-history-elt] narrows that swap root.
document.body.addEventListener("htmx:afterSettle", syncBodyScrollLock);
window.addEventListener("pageshow", syncBodyScrollLock);
document.body.addEventListener("htmx:historyRestore", syncBodyScrollLock);

// In-place navigation opens the new page at the top: the shell scrolls its
// content pane, not the window, so htmx would otherwise keep the old page's
// offset (see nav_scroll.ts). beforeHistoryUpdate fires right before the swap
// and carries both the history type and the final swap target.
document.body.addEventListener("htmx:beforeHistoryUpdate", ((event: CustomEvent) => {
    resetScrollOnNavigation(event.detail, document);
}) as EventListener);

const sseLifecycle = new SSELifecycle<EventSource>(
    async (sseURL, tokenURL) => {
        const sseConnection = new SSEConnection(sseURL, tokenURL, true);
        await sseConnection.connect();
        // sse.ts closes the stream on the server's "close" event; release it
        // here too, or the lifecycle keeps a dead source and never reconnects.
        sseConnection.evtSource?.addEventListener("close", () => sseLifecycle.stop());
        return sseConnection.evtSource;
    },
    (evtSource) => {
        if (window.__toastHandler) {
            window.__toastHandler.addEventSource(evtSource);
        }

        window.__sseConnection = evtSource;
        document.dispatchEvent(new CustomEvent("sse:connected", { detail: evtSource }));
    },
    () => {
        window.__sseConnection = null;
    },
);
sseLifecycle.bind(window);

export function startSSEConnection(sseURL: string, tokenURL: string): void {
    console.log('Starting SSE connection to:', sseURL);
    sseLifecycle.start(sseURL, tokenURL);
}

window.startSSEConnection = startSSEConnection;

document.addEventListener("DOMContentLoaded", () => {
    const searchInput = document.getElementById("search-input") as HTMLInputElement | null;
    const searchButton = document.getElementById("search-button");

    function focusSearchInput(): void {
        if (!searchInput) return;
        searchInput.focus();
    }

    if (searchButton) {
        searchButton.addEventListener("click", focusSearchInput);
    }

    document.addEventListener("keydown", (event: KeyboardEvent) => {
        if (!searchInput) return;
        if (shouldIgnoreGlobalShortcut(event, document, "palette")) return;
        if ((event.metaKey || event.ctrlKey) && event.key === "k") {
            event.preventDefault();
            focusSearchInput();
        }
    });

    document.addEventListener("keydown", (event: KeyboardEvent) => {
        if (!searchInput) return;
        if (shouldIgnoreGlobalShortcut(event, document, "slash")) return;
        if (event.key === "/" && document.activeElement !== searchInput) {
            event.preventDefault();
            focusSearchInput();
        }
    });
});
